import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

interface TooltipProps {
  children: React.ReactNode
  title: string
  description?: string
  position?: 'top' | 'bottom'
  align?: 'left' | 'right' | 'center'
  className?: string
  /** If true, tooltip can be toggled by clicking/tapping (for mobile). Default: false */
  showOnClick?: boolean
}

export function Tooltip({
  children,
  title,
  description,
  position = 'bottom',
  align = 'right',
  className,
  showOnClick = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on click outside (for mobile) - only if showOnClick is enabled
  useEffect(() => {
    if (!isVisible || !showOnClick) return

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isVisible, showOnClick])

  const handleClick = () => {
    if (showOnClick) {
      setIsVisible(!isVisible)
    }
  }

  return (
    <div ref={containerRef} className="relative group/tooltip">
      <div onClick={handleClick}>
        {children}
      </div>
      <div
        className={cn(
          'absolute px-3 py-2 rounded-md bg-[#1a1a1a] border border-white/[0.1] whitespace-nowrap transition-all duration-200 z-50 pointer-events-none',
          // Show on hover (desktop) or click (both)
          'opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible',
          // Show on click/tap (controlled by state) - only when showOnClick is enabled
          showOnClick && isVisible && 'opacity-100 !visible',
          position === 'bottom' && 'top-full mt-2',
          position === 'top' && 'bottom-full mb-2',
          align === 'right' && 'right-0',
          align === 'left' && 'left-0',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          className
        )}
      >
        <p className="text-xs font-medium text-white">{title}</p>
        {description && (
          <p className="text-xs text-[#888] mt-0.5">{description}</p>
        )}
      </div>
    </div>
  )
}
